import { useGetAllCourses, useGetUniversitiesLight, useSearchUsersByName } from '@actions';
import TableHeaderInfoCard from '@components/V3/TableHeaderInfoCard';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { useDebounce } from '@hooks';
import { StudentTier, STUDENT_TIER_OPTIONS, TriState } from '@shared/clientTypes/misc';
import { GOAL_STATUS_OPTIONS, NO_UNIVERSITY_FILTER_VALUE, STUDENT_STATUS } from '@shared/constants';
import { GoalStatusTypes, UserRole } from '@shared/enums';
import AgoraSearchBar from '@utils/AgoraSearchbar';
import { useGetAllMajorNames } from 'apps/agora/src/actions/major.actions';
import moment from 'moment';
import { ReactNode, useState } from 'react';
import { COLORS } from '../../../v3/global';
import './students-table-header.styles.scss';
import AgoraDatePicker from '../Utils/InputsV3/AgoraDatePicker';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';

type Props = {
  studentStatistics?: any;
  search?: string;
  graduationYears?: string[];
  handleSearch: (searchValue: string) => void;
  handleFilterBy: (status: string | undefined) => void;
  handleEnrolledIn: (course: string | undefined) => void;
  handleMentoredBy: (mentor: string | undefined) => void;
  handleAddGraduationYear: (gradYear: string) => void;
  handleRemoveGraduationYear: (gradYear: string) => void;
  handleClearGraduationYears: () => void;
  tiers: StudentTier[];
  handleAddTier: (tier: StudentTier) => void;
  handleRemoveTier: (tier: StudentTier) => void;
  handleClearTiers: () => void;
  applicationStages: GoalStatusTypes[];
  handleAddApplicationStage: (stage: GoalStatusTypes) => void;
  handleRemoveApplicationStage: (stage: GoalStatusTypes) => void;
  handleClearApplicationStages: () => void;
  universities: string[];
  handleAddUniversity: (university: string) => void;
  handleRemoveUniversity: (university: string) => void;
  handleClearUniversities: () => void;
  majors: string[];
  handleAddMajor: (major: string) => void;
  handleRemoveMajor: (major: string) => void;
  handleClearMajors: () => void;
  handleExtraFilter: (extraFilter: any) => void;
  isTestUserHandler: (value: TriState) => void;
  isDeactivatedHandler: (value: TriState) => void;
  isDeactivated?: boolean;
  isTestUser?: boolean;
  hasCoachMe?: boolean;
  hasCoachMeHandler: (value: TriState) => void;
  onExport?: () => void;
  isExporting: boolean;
  onSyncContracts?: () => void;
  isSyncingContracts?: boolean;
  extraFilters?: ReactNode;
  extraFilter: any;
  isLoadingStudentStatistics: boolean;
  handleClearStatuses?: () => void;
};

const StudentsTableHeader = ({
  studentStatistics,
  search,
  graduationYears,
  handleSearch,
  handleFilterBy,
  handleEnrolledIn,
  handleMentoredBy,
  handleAddGraduationYear,
  handleRemoveGraduationYear,
  handleClearGraduationYears,
  tiers,
  handleAddTier,
  handleRemoveTier,
  handleClearTiers,
  applicationStages,
  handleAddApplicationStage,
  handleRemoveApplicationStage,
  handleClearApplicationStages,
  universities,
  handleAddUniversity,
  handleRemoveUniversity,
  handleClearUniversities,
  majors,
  handleAddMajor,
  handleRemoveMajor,
  handleClearMajors,
  handleExtraFilter,
  isDeactivatedHandler,
  isTestUserHandler,
  isTestUser,
  isDeactivated,
  hasCoachMe,
  hasCoachMeHandler,
  onExport,
  isExporting,
  onSyncContracts,
  isSyncingContracts,
  extraFilters,
  extraFilter,
  handleClearStatuses,
  isLoadingStudentStatistics,
}: Props) => {
  const { data: allCourses } = useGetAllCourses();

  const [mentorFullName, setMentorFullName] = useState('');
  const [universitySearch, setUniversitySearch] = useState('');
  const [showExtraFilters, setShowExtraFilters] = useState(false);

  const debouncedUniversitySearch = useDebounce(universitySearch, 500);

  const { data: allMentors } = useSearchUsersByName({
    name: mentorFullName,
    role: UserRole.Mentor,
  });

  const { data: allUniversities, isLoading: isUniversitiesLoading } = useGetUniversitiesLight({
    search: debouncedUniversitySearch,
    limit: 20,
  });

  const { data: allMajors } = useGetAllMajorNames();

  const currentYear = moment().year();
  const years = [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7].map((value) => currentYear + value);

  return (
    <div
      className="pt-6 pr-8 pb-8 pl-8 "
      style={{
        backgroundColor: COLORS.DARK_GREY_ALT,
      }}
    >
      <div className="w-full flex flex-col">
        {/*HEADER TITLE AND BUTTON*/}
        <div className="flex justify-between items-center mb-6">
          <h1 style={{ color: COLORS.TEXT_PRIMARY }} className="header-title">
            Students
          </h1>
          <div className="flex items-center gap-3">
            {onSyncContracts ? (
              <Button
                variant="secondary"
                buttonText={isSyncingContracts ? 'Syncing HubSpot...' : 'Sync HubSpot contracts'}
                isLoading={isSyncingContracts}
                disabled={isSyncingContracts}
                onClick={onSyncContracts}
              />
            ) : null}
            {onExport ? (
              <Button
                variant="secondary"
                buttonText={isExporting ? 'Exporting...' : 'Export CSV'}
                isLoading={isExporting}
                disabled={isExporting}
                onClick={onExport}
              />
            ) : null}
          </div>
        </div>

        <div style={{ maxWidth: '1920px' }}>
          {/*TABLE HEADER INFO*/}
          {!isLoadingStudentStatistics ? (
            <div className="flex justify-between flex-wrap mb-4">
              <TableHeaderInfoCard
                title={studentStatistics?.inBetweenCourses ?? 'N/A'}
                description={'In-between courses'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
              />
              <TableHeaderInfoCard
                title={studentStatistics?.onGoingCourse ?? 'N/A'}
                description={'Ongoing course'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
              />
              <TableHeaderInfoCard
                title={studentStatistics?.courseCompleted ?? 'N/A'}
                description={'Courses completed'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
              />
              <TableHeaderInfoCard
                title={studentStatistics?.noMentorAssigned ?? 'N/A'}
                description={'No mentor assigned'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
              />
              <TableHeaderInfoCard
                title={studentStatistics?.needsMentorNow ?? 'N/A'}
                description={'Needs mentor now'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
              />
              <TableHeaderInfoCard
                title={studentStatistics?.mentorAllocated ?? 'N/A'}
                description={'Mentor allocated'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
              />
              <TableHeaderInfoCard
                title={studentStatistics?.firstSessionScheduled ?? 'N/A'}
                description={'First session scheduled'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
              />
              <TableHeaderInfoCard
                title={studentStatistics?.firstSessionCompleted ?? 'N/A'}
                description={'First session completed'}
                handleExtraFilter={handleExtraFilter}
                extraFilter={extraFilter}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center py-10">
              <LoadingSpinner />
            </div>
          )}

          {/*SEARCHBAR*/}
          <AgoraSearchBar
            search={search}
            handleSearch={handleSearch}
            placeholder={'Search by name, email, phone number...'}
          />

          <div className="flex flex-wrap gap-4">
            {/*FILTER BY*/}
            <Select
              variant="secondary"
              className="max-w-64"
              label="Filter By"
              onSelect={(value) => handleFilterBy(value as string)}
              options={STUDENT_STATUS.map((value, index) => ({
                value,
                label: value,
              }))}
              onClear={() => handleFilterBy(undefined)}
            />

            {/*ENROLLED IN*/}
            <Select
              variant="secondary"
              className="max-w-64"
              label="Enrolled In"
              onSelect={(value) => handleEnrolledIn(value as string)}
              options={allCourses?.map((value) => ({
                value: value._id,
                label: value.title,
              }))}
              onClear={() => handleEnrolledIn(undefined)}
            />

            {/*MENTORED BY*/}
            <Select
              variant="secondary"
              className="max-w-64"
              label={'Mentored By'}
              onSelect={(value) => handleMentoredBy(value as string)}
              onSearch={(value) => setMentorFullName(value)}
              options={allMentors?.map((value) => ({
                value: value._id,
                label: value.fullName,
              }))}
              onClear={() => handleMentoredBy(undefined)}
            />

            <div className="flex flex-col w-64 max-w-64">
              <Select
                className="max-w-64"
                variant="secondary"
                label="Graduation Year"
                mode="multiple"
                value={graduationYears}
                onSelect={(value) => typeof value === 'string' && handleAddGraduationYear(value)}
                onDeselect={handleRemoveGraduationYear}
                onClear={handleClearGraduationYears}
                options={years.map((gradYear: number) => ({
                  value: gradYear.toString(),
                  label: gradYear.toString(),
                }))}
              />
            </div>
            <div className="flex flex-col w-64 max-w-64">
              <Select
                className="max-w-64"
                variant="secondary"
                label="Tier"
                mode="multiple"
                value={tiers.length > 0 ? tiers : undefined}
                options={STUDENT_TIER_OPTIONS}
                onSelect={(value) => {
                  if (value === null || typeof value === 'undefined') {
                    return;
                  }
                  handleAddTier(value as StudentTier);
                }}
                onDeselect={(value) => {
                  if (value === null || typeof value === 'undefined') {
                    return;
                  }
                  handleRemoveTier(value as StudentTier);
                }}
                onClear={handleClearTiers}
              />
            </div>
            <div className="flex flex-col w-64 max-w-64">
              <Select
                className="max-w-64"
                variant="secondary"
                label="Application Stage"
                mode="multiple"
                value={applicationStages.length > 0 ? applicationStages : undefined}
                options={GOAL_STATUS_OPTIONS}
                onSelect={(value) => {
                  if (value === null || typeof value === 'undefined') {
                    return;
                  }
                  handleAddApplicationStage(value as GoalStatusTypes);
                }}
                onDeselect={(value) => {
                  if (value === null || typeof value === 'undefined') {
                    return;
                  }
                  handleRemoveApplicationStage(value as GoalStatusTypes);
                }}
                onClear={handleClearApplicationStages}
              />
            </div>
            <div className="flex flex-col w-64 max-w-64">
              <Select
                className="max-w-64"
                variant="secondary"
                label="University"
                mode="multiple"
                value={universities.length > 0 ? universities : undefined}
                filterOption={() => true}
                searchValue={universitySearch}
                onSearch={setUniversitySearch}
                onBlur={() => setUniversitySearch('')}
                onSelect={(value) => handleAddUniversity(value as string)}
                onDeselect={(value) => handleRemoveUniversity(value as string)}
                onClear={handleClearUniversities}
                options={[
                  { value: NO_UNIVERSITY_FILTER_VALUE, label: 'No university' },
                  ...(allUniversities?.map((university) => ({
                    value: university._id,
                    label: university.name,
                  })) || []),
                ]}
                notFoundContent={
                  isUniversitiesLoading ? (
                    <LoadingSpinner className="mx-auto" size="normal" />
                  ) : null
                }
                placeholder="Search Universities"
              />
            </div>
            <div className="flex flex-col w-64 max-w-64">
              <Select
                className="max-w-64"
                variant="secondary"
                label="Major"
                mode="multiple"
                value={majors.length > 0 ? majors : undefined}
                onSelect={(value) => handleAddMajor(value as string)}
                onDeselect={(value) => handleRemoveMajor(value as string)}
                onClear={handleClearMajors}
                options={(allMajors ?? [])
                  .filter((major) => !!major._id)
                  .map((major) => ({
                    value: major._id as string,
                    label: major.name,
                  }))}
                placeholder="Search Majors"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-6 mt-6">
            <TriStateToggle
              title="Show Test Users:"
              options={[
                { label: 'Regular', value: 'unchecked' },
                { label: 'Test', value: 'checked' },
                { label: 'All', value: 'indeterminate' },
              ]}
              value={isTestUser}
              onChange={isTestUserHandler}
            />
            <TriStateToggle
              title="Active Status:"
              options={[
                { label: 'Active', value: 'unchecked' },
                { label: 'Deactivated', value: 'checked' },
                { label: 'All', value: 'indeterminate' },
              ]}
              value={isDeactivated}
              onChange={isDeactivatedHandler}
            />
            <TriStateToggle
              title="CoachMe course:"
              options={[
                { label: 'Without', value: 'unchecked' },
                { label: 'With', value: 'checked' },
                { label: 'All', value: 'indeterminate' },
              ]}
              value={hasCoachMe}
              onChange={hasCoachMeHandler}
            />
            {extraFilters ? (
              <button
                type="button"
                className="self-end pb-1 text-sm text-customPrimary hover:underline"
                onClick={() => setShowExtraFilters((value) => !value)}
              >
                {showExtraFilters ? 'Hide contract filters' : 'Contract filters (HubSpot)'}
              </button>
            ) : null}
          </div>
          {extraFilters && showExtraFilters ? <div className="mt-6">{extraFilters}</div> : null}
        </div>
      </div>
    </div>
  );
};

export default StudentsTableHeader;
